import React from 'react';
import Icon from '@components/AppIcon';

const CultureSection = () => {
  const cultureValues = [
    {
      icon: 'Heart',
      title: 'Patient-Centered Care',
      description: 'Every decision we make is guided by what\'s best for our patients. We believe in compassionate, personalized healthcare that puts people first.',
      gradient: 'from-red-500/10 to-pink-500/10',
      iconColor: 'text-red-500'
    },
    {
      icon: 'Users',
      title: 'Collaborative Environment', 
      description: 'We work as a unified team where every voice matters. Our collaborative culture fosters innovation and professional growth.',
      gradient: 'from-blue-500/10 to-cyan-500/10',
      iconColor: 'text-[#08005f]'
    },
    {
      icon: 'Award',
      title: 'Excellence in Practice',
      description: 'We maintain the highest standards in medical care, continuously improving our skills and embracing new technologies and methods.',
      gradient: 'from-yellow-500/10 to-orange-500/10',
      iconColor: 'text-yellow-600'
    },
    {
      icon: 'BookOpen',
      title: 'Continuous Learning',
      description: 'We invest in our team\'s professional development through training, conferences, and educational opportunities.',
      gradient: 'from-green-500/10 to-emerald-500/10',
      iconColor: 'text-green-500'
    },
    {
      icon: 'Zap',
      title: 'Innovation First',
      description: 'We embrace cutting-edge technology and innovative approaches to deliver the best possible patient outcomes.',
      gradient: 'from-purple-500/10 to-indigo-500/10',
      iconColor: 'text-purple-500'
    },
    {
      icon: 'Shield',
      title: 'Ethical Standards',
      description: 'We uphold the highest ethical standards in all our practices, ensuring trust and integrity in everything we do.',
      gradient: 'from-teal-500/10 to-cyan-500/10',
      iconColor: 'text-teal-500'
    }
  ];

  return (
    <section className="py-20 md:py-28 bg-gradient-to-br from-gray-50 to-white relative overflow-hidden">
      {/* Background Decorations */}
      <div className="absolute inset-0">
        <div className="absolute top-10 left-10 w-40 h-40 bg-primary/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 right-10 w-32 h-32 bg-accent/5 rounded-full blur-2xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-60 h-60 bg-secondary/5 rounded-full blur-3xl"></div>
      </div>

      <div className="relative container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center p-2 bg-primary/10 rounded-full mb-6">
            <Icon name="Sparkles" size={24} className="text-primary" />
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6" style={{ color: "#08005f" }}>
            Why Choose 
            <span className="block bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent" style={{ color: "#08005f" }}>
              Eye&U Kenya?
            </span>
          </h2>
          <p className="text-lg md:text-xl text-text-secondary max-w-3xl mx-auto leading-relaxed">
            Join a workplace culture that values excellence, collaboration, and personal growth. 
            We're not just a team—we're a family dedicated to making a difference.
          </p>
        </div>

        {/* Culture Values Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {cultureValues?.map((value, index) => (
            <div
              key={index}
              className="group relative"
            >
              {/* Card */}
              <div className={`relative h-full bg-gradient-to-br ${value?.gradient} backdrop-blur-sm rounded-2xl p-8 border border-white/20 shadow-lg hover:shadow-2xl transition-all duration-500 hover:scale-105 hover:-translate-y-2`}>
                {/* Icon */}
                <div className="relative mb-6">
                  <div className="absolute inset-0 bg-white/50 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  <div className="relative w-16 h-16 bg-white/80 rounded-2xl flex items-center justify-center group-hover:rotate-12 transition-transform duration-500">
                    <Icon name={value?.icon} size={32} className={value?.iconColor} />
                  </div>
                </div>

                {/* Content */}
                <h3 className="text-xl font-bold text-foreground mb-4 group-hover:text-primary transition-colors duration-300">
                  {value?.title}
                </h3>
                <p className="text-text-secondary leading-relaxed text-sm">
                  {value?.description}
                </p>

                {/* Decorative Elements */}
                <div className="absolute top-4 right-4 w-2 h-2 bg-primary/30 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                <div className="absolute bottom-4 left-4 w-1 h-1 bg-accent/40 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
              </div>
            </div>
          ))}
        </div>

        {/* Call to Action */}
        <div className="text-center mt-16">
          <div className="bg-gradient-to-r from-primary/10 via-accent/10 to-secondary/10 rounded-2xl p-8 md:p-12 max-w-4xl mx-auto border border-white/20 shadow-lg">
            <h3 className="text-2xl md:text-3xl font-bold text-foreground mb-4" style={{ color: "#08005f" }}>
              Ready to Make a Difference?
            </h3>
            <p className="text-lg text-text-secondary mb-6 max-w-2xl mx-auto">
              Join our mission to transform lives through exceptional eye care. Your career journey with us starts here.
            </p>
            <div className="flex items-center justify-center space-x-2 text-primary">
              <Icon name="ArrowDown" size={20} className="animate-bounce" />
              <span className="font-medium">Explore opportunities below</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CultureSection;